/**
 * RFST — Product page behaviour
 * Variant selection, gallery + zoom modal, share.
 * Adding to cart itself is handled by cart.js (delegated [data-product-form] submit).
 */
(function () {
  'use strict';

  var section = document.querySelector('[data-product-section]');
  if (!section) return;

  /* -------------------------------------------------- *
   * Variants
   * -------------------------------------------------- */

  var variantsScript = section.querySelector('[data-product-variants]');
  var variants = [];
  if (variantsScript) {
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      variants = [];
    }
  }

  var picker = section.querySelector('[data-variant-picker]');
  var idInput = section.querySelector('[data-variant-id-input]');
  var addButton = section.querySelector('[data-add-to-cart]');
  var addButtonText = section.querySelector('[data-add-to-cart-text]');
  var priceWrapper = section.querySelector('[data-price-wrapper]');

  function getSelectedOptions() {
    if (!picker) return [];
    return Array.prototype.map.call(picker.querySelectorAll('.variant-picker__input:checked'), function (input) {
      return input.value;
    });
  }

  function findVariant(selectedOptions) {
    return variants.find(function (variant) {
      return variant.options.every(function (option, index) {
        return option === selectedOptions[index];
      });
    });
  }

  function updateSelectedValueLabels() {
    if (!picker) return;
    picker.querySelectorAll('.variant-picker__input:checked').forEach(function (input) {
      var position = input.getAttribute('data-option-position');
      var label = picker.querySelector('[data-option-selected-value="' + position + '"]');
      if (label) label.textContent = input.value;
    });
  }

  function updateUrl(variant) {
    if (!variant || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  function updatePrice(variant) {
    if (!priceWrapper || !variant) return;

    var sectionId = section.closest('.shopify-section');
    if (!sectionId) return;
    var id = sectionId.id.replace('shopify-section-', '');

    fetch(window.location.pathname + '?variant=' + variant.id + '&section_id=' + id)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newPrice = parsed.querySelector('[data-price-wrapper]');
        if (newPrice) priceWrapper.innerHTML = newPrice.innerHTML;
      })
      .catch(function () {
        /* Price stays as-is if the request fails. */
      });
  }

  function updateMediaForVariant(variant) {
    if (!variant || !variant.featured_media) return;
    showMedia(String(variant.featured_media.id));
  }

  function onVariantChange() {
    var selectedOptions = getSelectedOptions();
    var variant = findVariant(selectedOptions);

    updateSelectedValueLabels();

    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (addButtonText) addButtonText.textContent = window.RFSTStrings.unavailable;
      if (idInput) idInput.disabled = true;
      return;
    }

    if (idInput) {
      idInput.value = variant.id;
      idInput.disabled = !variant.available;
    }

    if (addButton) addButton.disabled = !variant.available;
    if (addButtonText) {
      addButtonText.textContent = variant.available ? window.RFSTStrings.addToCart : window.RFSTStrings.soldOut;
    }

    updateUrl(variant);
    updatePrice(variant);
    updateMediaForVariant(variant);
  }

  if (picker) {
    picker.addEventListener('change', onVariantChange);
  }

  /* -------------------------------------------------- *
   * Gallery
   * -------------------------------------------------- */

  var gallery = section.querySelector('[data-product-gallery]');

  function showMedia(mediaId) {
    if (!gallery) return;

    gallery.querySelectorAll('[data-gallery-main-item]').forEach(function (item) {
      var isTarget = item.getAttribute('data-media-id') === mediaId;
      item.hidden = !isTarget;
      item.classList.toggle('is-active', isTarget);
    });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      var isTarget = thumb.getAttribute('data-media-id') === mediaId;
      thumb.classList.toggle('is-active', isTarget);
      if (isTarget) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  if (gallery) {
    gallery.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        showMedia(thumb.getAttribute('data-media-id'));
        return;
      }

      var mainItem = event.target.closest('[data-gallery-main-item]');
      if (mainItem) {
        openZoom(mainItem.getAttribute('data-zoom-src'));
      }
    });
  }

  /* -------------------------------------------------- *
   * Zoom modal
   * -------------------------------------------------- */

  var zoomModal = document.querySelector('[data-zoom-modal]');
  var zoomImage = zoomModal && zoomModal.querySelector('[data-zoom-image]');

  function openZoom(src) {
    if (!zoomModal || !zoomImage || !src) return;

    zoomImage.setAttribute('src', src);
    zoomModal.hidden = false;
    void zoomModal.offsetWidth;
    zoomModal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    var closeButton = zoomModal.querySelector('[data-zoom-close]');
    if (closeButton) closeButton.focus();

    document.addEventListener('keydown', onZoomKeydown);
  }

  function closeZoom() {
    if (!zoomModal) return;

    zoomModal.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onZoomKeydown);

    window.setTimeout(function () {
      if (!zoomModal.classList.contains('is-open')) {
        zoomModal.hidden = true;
        if (zoomImage) zoomImage.setAttribute('src', '');
      }
    }, 250);
  }

  function onZoomKeydown(event) {
    if (event.key === 'Escape') closeZoom();
  }

  if (zoomModal) {
    zoomModal.addEventListener('click', function (event) {
      if (event.target.closest('[data-zoom-close]')) {
        closeZoom();
      }
    });
  }

  /* -------------------------------------------------- *
   * Share
   * -------------------------------------------------- */

  var shareButton = section.querySelector('[data-share-button]');
  if (shareButton) {
    shareButton.addEventListener('click', function () {
      var url = shareButton.getAttribute('data-share-url');
      var title = shareButton.getAttribute('data-share-title');
      var feedback = section.querySelector('[data-share-feedback]');

      function showFeedback() {
        if (!feedback) return;
        feedback.hidden = false;
        window.setTimeout(function () {
          feedback.hidden = true;
        }, 2000);
      }

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(showFeedback).catch(function () {});
      }
    });
  }

  /* -------------------------------------------------- *
   * Sticky buy bar (mobile)
   * -------------------------------------------------- */

  var stickyBar = document.querySelector('[data-product-sticky-bar]');

  if (stickyBar && addButton && 'IntersectionObserver' in window) {
    var stickyAdd = stickyBar.querySelector('[data-sticky-add]');
    var stickyVariant = stickyBar.querySelector('[data-sticky-variant]');

    function showStickyBar() {
      if (!stickyBar.hidden) return;
      stickyBar.hidden = false;
      void stickyBar.offsetWidth;
      stickyBar.classList.add('is-visible');
    }

    function hideStickyBar() {
      if (stickyBar.hidden) return;
      stickyBar.classList.remove('is-visible');
      window.setTimeout(function () {
        if (!stickyBar.classList.contains('is-visible')) stickyBar.hidden = true;
      }, 250);
    }

    // La barre ne sert qu'une fois le bouton principal sorti de l'écran.
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hideStickyBar();
          } else if (entry.boundingClientRect.top < 0) {
            showStickyBar();
          } else {
            hideStickyBar();
          }
        });
      },
      { threshold: 0 }
    ).observe(addButton);

    // Le bouton flottant reflète l'état du bouton principal.
    if (picker && stickyAdd) {
      picker.addEventListener('change', function () {
        stickyAdd.disabled = addButton.disabled;
        if (addButtonText) stickyAdd.textContent = addButtonText.textContent;
        if (stickyVariant) {
          stickyVariant.textContent = getSelectedOptions().join(' / ');
        }
      });
    }
  }
})();
